import React from 'react';
import { DeploymentConfig } from '../Model/Deployment';

interface StepThreeProps {
  config: DeploymentConfig;
  onBack: () => void;
  onDeploy: () => Promise<void>;
  deploymentStatus: 'idle' | 'deploying' | 'success' | 'error';
  isDeploying: boolean;
  deploymentId: string;
  monitorDeployment: boolean;
  setMonitorDeployment: React.Dispatch<React.SetStateAction<boolean>>;
}

// ── Review row ────────────────────────────────────────────────────────────────

const ReviewRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #26262f' }}>
    <span style={{ fontSize: '11.5px', color: '#52525f', fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '.04em' }}>{label}</span>
    <span style={{ fontSize: '13px', color: '#eeeef5', fontWeight: 500, textAlign: 'right' as const }}>{value}</span>
  </div>
);

// ── Section card ──────────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ background: '#17171e', border: '1px solid #26262f', borderRadius: '8px', padding: '14px 16px' }}>
    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>
      {title}
    </div>
    {children}
  </div>
);

// ── StepThree ─────────────────────────────────────────────────────────────────

const StepThree: React.FC<StepThreeProps> = ({
  config,
  onBack,
  onDeploy,
  deploymentStatus,
  isDeploying,
  deploymentId,
  monitorDeployment,
  setMonitorDeployment,
}) => {
  const hasEnvVars = Object.keys(config.env_vars).length > 0;

  const badge = (value: boolean, trueLabel = 'Yes', falseLabel = 'No') => (
    <span style={{
      display: 'inline-flex', padding: '2px 8px', borderRadius: '20px',
      fontSize: '10.5px', fontWeight: 600,
      background: value ? 'rgba(99,102,241,.14)' : '#1e1e27',
      color: value ? '#818cf8' : '#52525f',
      border: `1px solid ${value ? 'rgba(99,102,241,.2)' : '#26262f'}`,
    }}>
      {value ? trueLabel : falseLabel}
    </span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ marginBottom: '4px' }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>
          Review & Deploy
        </div>
        <div style={{ fontSize: '12px', color: '#52525f' }}>Verify your configuration before deploying</div>
      </div>

      {/* App details */}
      <Section title="Application Details">
        <ReviewRow label="App Name" value={config.app_name || '—'} />
        <ReviewRow label="Repository" value={
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: '#8e8ea8' }}>{config.repo_name || '—'}</span>
        }/>
        <ReviewRow label="Branch" value={
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: '#818cf8' }}>{config.branch || 'main'}</span>
        }/>
        <ReviewRow label="Environment" value={
          <span style={{
            display: 'inline-flex', padding: '2px 8px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
            background: config.deployment_env === 'production' ? 'rgba(244,63,94,.1)' : config.deployment_env === 'staging' ? 'rgba(234,179,8,.1)' : 'rgba(34,197,94,.1)',
            color: config.deployment_env === 'production' ? '#f43f5e' : config.deployment_env === 'staging' ? '#eab308' : '#22c55e',
          }}>
            {config.deployment_env || 'development'}
          </span>
        }/>
        <div style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <ReviewRow label="Cloud Provider" value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px', background: 'rgba(255,153,0,.12)', color: '#ff9900' }}>
              {config.selected_cloud || 'AWS'}
            </span>
          }/>
        </div>
      </Section>

      {/* Configuration */}
      <Section title="Configuration">
        <ReviewRow label="Service" value={
          <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600, background: '#1e1e27', color: '#8e8ea8', border: '1px solid #26262f' }}>
            {config.service_selected || '—'}
          </span>
        }/>
        <ReviewRow label="CI/CD" value={badge(!!config.ci_cd, 'Enabled', 'Disabled')} />
        <div style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <ReviewRow label="Monitoring" value={badge(!!config.monitoring, 'Enabled', 'Disabled')} />
        </div>
      </Section>

      {/* Resources */}
      <Section title="Resources">
        <ReviewRow label="CPU" value={`${config.resources.cpu} Cores`} />
        <ReviewRow label="Memory" value={`${config.resources.memory} GB`} />
        <ReviewRow label="Min Instances" value={config.resources.minInstances} />
        <div style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <ReviewRow label="Max Instances" value={config.resources.maxInstances} />
        </div>
      </Section>

      {/* Env vars */}
      <Section title="Environment Variables">
        {hasEnvVars ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {Object.entries(config.env_vars).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', background: '#111116', borderRadius: '5px' }}>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: '#818cf8', minWidth: '120px' }}>{key}</span>
                <span style={{ color: '#52525f', fontSize: '11px' }}>=</span>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: '#8e8ea8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                  {String(value).length > 30 ? `${String(value).substring(0, 30)}…` : String(value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '12.5px', color: '#52525f', fontStyle: 'italic' as const }}>No environment variables configured</div>
        )}
      </Section>

      {/* Deploy status */}
      {deploymentStatus !== 'idle' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
          borderRadius: '6px', fontSize: '13px',
          background: deploymentStatus === 'deploying' ? 'rgba(59,130,246,.1)' : deploymentStatus === 'success' ? 'rgba(34,197,94,.1)' : 'rgba(244,63,94,.1)',
          border: `1px solid ${deploymentStatus === 'deploying' ? 'rgba(59,130,246,.25)' : deploymentStatus === 'success' ? 'rgba(34,197,94,.25)' : 'rgba(244,63,94,.25)'}`,
          color: deploymentStatus === 'deploying' ? '#3b82f6' : deploymentStatus === 'success' ? '#22c55e' : '#f43f5e',
        }}>
          {deploymentStatus === 'deploying' && (
            <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
          )}
          {deploymentStatus === 'success' && (
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 7.5l4.5 4.5 6.5-8"/></svg>
          )}
          {deploymentStatus === 'error' && (
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7.5" cy="7.5" r="6"/><path d="M7.5 4.5v3M7.5 10v.5"/></svg>
          )}
          {deploymentStatus === 'deploying' ? 'Deploying your application…' : deploymentStatus === 'success' ? 'Deployment successful!' : 'Deployment failed. Please try again.'}
        </div>
      )}

      {/* Navigation */}
      <div style={{ paddingTop: '8px', borderTop: '1px solid #26262f', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={onBack}
          disabled={isDeploying}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '8px 16px', borderRadius: '6px', fontSize: '13px',
            fontWeight: 500, cursor: isDeploying ? 'not-allowed' : 'pointer',
            background: '#17171e', border: '1px solid #32323d', color: '#eeeef5',
            opacity: isDeploying ? 0.5 : 1,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M10.5 6.5h-8M6 3.5L2.5 6.5 6 10"/>
          </svg>
          Back
        </button>

        <button
          onClick={onDeploy}
          disabled={isDeploying || deploymentStatus === 'success'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            padding: '8px 24px', borderRadius: '6px', fontSize: '13px',
            fontWeight: 600, cursor: isDeploying || deploymentStatus === 'success' ? 'not-allowed' : 'pointer',
            border: 'none', fontFamily: "'Inter',sans-serif",
            background: deploymentStatus === 'success' ? '#22c55e' : isDeploying ? '#3d3f6b' : '#6366f1',
            color: '#fff',
            opacity: isDeploying ? 0.7 : 1,
          }}
        >
          {isDeploying && (
            <svg style={{ animation: 'spin .8s linear infinite' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
          )}
          {isDeploying ? 'Deploying…' : deploymentStatus === 'success' ? '✓ Deployed' : '🚀 Deploy Now'}
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </button>
      </div>
    </div>
  );
};

export default StepThree;